import { z } from 'zod';

// productPricing.computeRow — pure, no DB writes. Given a row's raw inputs
// + the field the user last edited, the BE applies the math from
// apps/products/prd/product-edit/05-tab-pricing.md §"Pricing math (single
// source of truth)" and returns the derived display values. The FE never
// ports this math — it only reads `cost`/`netCost`/`markup`/`price`/`priceIncl`
// from the result.
//
// Field naming matches the at-rest jsonb (stored on product.price):
//   cost      = "Net cost (incl. VAT)" input
//   netCost   = "Net cost (excl. VAT)" derived display
//   markup    = % markup (derived or input)
//   price     = "Price (excl. VAT)" input or derived
//   priceIncl = "Price (incl. VAT)" derived display
//   commission = % commission (gross mode only)

export const pricingRowMoneyInputSchema = z.object({
  cost: z.number().nonnegative().optional(),
  netCost: z.number().nonnegative().optional(),
  markup: z.number().optional(),
  price: z.number().nonnegative().optional(),
  priceIncl: z.number().nonnegative().optional(),
  commission: z.number().optional(),
});
export type PricingRowMoneyInput = z.infer<typeof pricingRowMoneyInputSchema>;

// Identifies which field the user just changed. Drives the recompute path
// ("last-edited field wins" rule from PRD §"Side effects on field change").
export const pricingRowLastEditedSchema = z.enum([
  'cost',
  'netCost',
  'markup',
  'price',
  'priceIncl',
  'commission',
  'costVat',
  'priceVat',
  'pricingType',
]);
export type PricingRowLastEdited = z.infer<typeof pricingRowLastEditedSchema>;

export const computeProductPricingRowInputSchema = z.object({
  pricingType: z.enum(['net', 'gross']),
  costVatId: z.uuid().nullable().optional(),
  priceVatId: z.uuid().nullable().optional(),
  // Direct VAT-percent overrides. When supplied, the BE skips the DB lookup
  // for the corresponding id — used by the FE's fallback rate list when the
  // org hasn't seeded vat_tax_setting rows yet. Negative values rejected.
  costVatPercent: z.number().nonnegative().nullable().optional(),
  priceVatPercent: z.number().nonnegative().nullable().optional(),
  lastEdited: pricingRowLastEditedSchema,
  row: pricingRowMoneyInputSchema,
});
export type ComputeProductPricingRowInput = z.infer<
  typeof computeProductPricingRowInputSchema
>;

// Product-domain lookups: surface org-scoped VAT + traveller-type rows
// without forcing product editors to carry `company_settings:read`. Same
// shape as the canonical CRUD endpoints but gated by `products:read`.

export const productPricingVatOptionSchema = z.object({
  id: z.uuid(),
  title: z.string().nullable(),
  // Drizzle numeric → string on the wire to preserve precision; FE parses
  // to number when forwarding as a computeRow override.
  percent: z.string().nullable(),
});
export type ProductPricingVatOption = z.infer<
  typeof productPricingVatOptionSchema
>;

export const listProductPricingVatOptionsResultSchema = z.object({
  data: z.array(productPricingVatOptionSchema),
});

export const productPricingTravellerTypeSchema = z.object({
  id: z.uuid(),
  title: z.string().nullable(),
});
export type ProductPricingTravellerType = z.infer<
  typeof productPricingTravellerTypeSchema
>;

export const listProductPricingTravellerTypesResultSchema = z.object({
  data: z.array(productPricingTravellerTypeSchema),
});

export const computeProductPricingRowResultSchema = z.object({
  cost: z.number().nullable(),
  netCost: z.number().nullable(),
  markup: z.number().nullable(),
  price: z.number().nullable(),
  priceIncl: z.number().nullable(),
  commission: z.number().nullable(),
  // Echoed back so the FE knows which VAT% the BE applied. Null when the VAT
  // id is unset or the row doesn't exist for the caller's org.
  costVatPercent: z.number().nullable(),
  priceVatPercent: z.number().nullable(),
});
export type ComputeProductPricingRowResult = z.infer<
  typeof computeProductPricingRowResultSchema
>;
